import { all, call, getContext, put, takeEvery } from 'redux-saga/effects';
import i18n from '../../../i18n';
import type { ILanguage, IProduct, ISettings, ISettingsKeystoreEntry } from '../../api/domain';
import type { I18ImportService, SettingsApiService } from '../../api/services';
import { parseToSettings } from '../../utils/map-settings';
import { logError } from '../logging-api/logging-api-actions';
import type { Action } from '../utils';
import {
    fetchLanguages,
    fetchLanguagesFailure,
    fetchLanguagesSuccess,
    fetchProducts,
    fetchProductsFailure,
    fetchProductsSuccess,
    fetchSettings,
    fetchSettingsFailure,
    fetchSettingsSuccess,
    setHeaderImageMetaData,
    setSettingsInitialized,
} from './settings-api-actions';

function* handleFetchSettings() {
    const api: SettingsApiService = yield getContext('settingsApiService');
    try {
        const keystoreEntries: ISettingsKeystoreEntry[] = yield call([api, api.getKeystoreEntries]);

        const settings: ISettings = parseToSettings(keystoreEntries);

        const headerImages = settings.header?.pictures || [];

        let headerImageMetaDataIdx = headerImages.findIndex((pic) => pic.default);
        if (headerImageMetaDataIdx === -1) headerImageMetaDataIdx = Math.floor(Math.random() * headerImages.length);

        const headerImageMetaData = headerImages[headerImageMetaDataIdx];

        yield put(setHeaderImageMetaData({ ...headerImageMetaData, idx: headerImageMetaDataIdx }));

        yield put(fetchSettingsSuccess(settings));
    } catch (e: unknown) {
        yield put(logError(`could not fetch settings: ${e}`));
        yield put(fetchSettingsFailure());
    }
}

function* handleFetchProducts({ payload: categories }: Action<string[]>) {
    const api: SettingsApiService = yield getContext('settingsApiService');
    try {
        const products: IProduct[] = yield call([api, api.getProducts], categories);
        yield put(fetchProductsSuccess({ products, categories }));
    } catch (e: unknown) {
        yield put(logError(`could not fetch products: ${e}`));
        yield put(fetchProductsFailure({ categories }));
    }
}

function importSpecialAdditionalTranslations(settings: ISettings) {
    // import footer contact(address) title translations
    settings.footer?.address?.forEach((addr) => {
        i18n.addResource(addr.languageCode as any, 'settings.footer.address.title', addr.title);
    });

    // import contact business-tel-hours items translations
    settings.contact?.businessTelHours?.forEach((hours) => {
        hours.items.forEach((item, index) => {
            Object.keys(item).forEach((key) => {
                i18n.addResource(hours.languageCode as any, `settings.contact.businessTelHours.${index}.${key}`, item[key]);
            });
        });
    });
}

// this imports settings values as translation keys
// TODO: we had issues with this because having key store settings added as i18next translations
// turned out to be pretty opaque - do not use logic like this for new code
function* importSettingsTranslations(action: Action<ISettings>) {
    const settings: ISettings = action.payload;
    const i18nImportService: I18ImportService = yield getContext('i18ImportService');

    importSpecialAdditionalTranslations(settings);

    i18nImportService.importTranslations({
        ...settings,
        header: {
            ...settings.header,
        },
    });

    yield put(setSettingsInitialized());
}

function* handleFetchLanguages() {
    const api: SettingsApiService = yield getContext('settingsApiService');
    try {
        const languages: ILanguage[] = yield call([api, api.getLanguages]);
        yield put(fetchLanguagesSuccess(languages));
    } catch (e: unknown) {
        yield put(logError(`could not fetch languages: ${e}`));
        yield put(fetchLanguagesFailure());
    }
}

export function* settingsApiSaga() {
    yield all([
        takeEvery(fetchSettings, handleFetchSettings),
        takeEvery(fetchProducts, handleFetchProducts),
        takeEvery(fetchSettingsSuccess, importSettingsTranslations),
        takeEvery(fetchLanguages, handleFetchLanguages),
    ]);
}
